import { PrevNext } from '@/components/CommonComponents/PrevNext';
import { Stepper } from '@/components/CommonComponents/Stepper';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import SurveyPreviewModal from '@/components/SurveyManagement/SurveyPreviewModal';
import SurveySuccessModal from '@/components/SurveyManagement/SurveySuccessModal';
import { uploadBufferToS3 } from '@/helper/fileUpload.helper';
import { showToast } from '@/helper/toast.helper';
import { sanitizeKey } from '@/helper/utils';
import { AppDispatch } from '@/store';
import {
  createSurvey,
  updateSurvey,
} from '@/store/slices/surveyManagementSlice/surveyManagementSlice';
import { SurveyListItem } from '@/types/survey.types';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import BasicInfo from './BasicInfo';
import SurveyReviewAndCreate from './ReviewAndCreate';

type FromSource = 'dashboard' | 'survey-management';
type NavState = { from?: FromSource; survey?: SurveyListItem };

const getQueryFrom = (search: string): FromSource | undefined => {
  const params = new URLSearchParams(search);
  const from = params.get('from');
  return from === 'dashboard' || from === 'survey-management' ? from : undefined;
};

const CreateSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [error, setError] = useState<Record<string, string>>({});

  const [resetKey, setResetKey] = useState(0);

  const from = useMemo<FromSource | undefined>(() => {
    const state = (location.state as NavState | null) ?? {};
    return state.from ?? getQueryFrom(location.search);
  }, [location.state, location.search]);

  const editSurvey = (location.state as NavState)?.survey;

  const [surveyData, setSurveyData] = useState<Partial<SurveyListItem>>({
    id: editSurvey?.id,
    title: editSurvey?.title || '',
    odkFileUrl: editSurvey?.odkFileUrl || '',
    status: editSurvey?.status || true,
    fileData: null,
  });
  const [odkFile, setOdkFile] = useState<File | null>(null);
  const [consentMsg, setConsentMsg] = useState<string>('');
  const [step, setStep] = useState<number>(0);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [previewSurvey, setPreviewSurvey] = useState<SurveyListItem | null>(null);

  const handleDownloadExistingFile = async () => {
    if (!surveyData.odkFileUrl) return;

    setIsDownloading(true);
    try {
      const response = await fetch(surveyData.odkFileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from URL
      const fileName = surveyData.odkFileUrl.split('/').pop() || 'survey-file';
      const decodedFileName = decodeURIComponent(fileName);
      // Remove timestamp prefix if present (e.g., "1763383357957_sample.xls" -> "sample.xls")
      const cleanFileName = decodedFileName.replace(/^\d+_/, '');

      link.download = cleanFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast.success('File downloaded successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast.error('Failed to download the file. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const isNextDisabled = useMemo(() => {
    if (step === 0) {
      const isTitleValid =
        surveyData.title?.trim() &&
        surveyData.title?.trim().length >= 5 &&
        surveyData.title?.trim().length <= 60;
      const isFileRequired = !editSurvey && !odkFile && !surveyData.fileData;
      return !isTitleValid || isFileRequired;
    }
    return false;
  }, [step, surveyData.title, editSurvey, odkFile, surveyData.fileData]);

  const handleBackStep = () => setStep((prev) => Math.max(prev - 1, 0));
  const handleNext = () => {
    if (step === 0) {
      if (!surveyData.title?.trim()) {
        setError({ title: 'Please enter a survey title' });
        return;
      }
      if (!editSurvey && !odkFile && !surveyData.fileData) {
        setError({ file: 'Please upload a survey file' });
        return;
      }
    }
    setStep((prev) => Math.min(prev + 1, 1));
  };

  const handleResetProperties = () => {
    setShowModal(false);
    setSurveyData({ title: '', odkFileUrl: '', status: true, fileData: null });
    setOdkFile(null);
    setConsentMsg('');
    setStep(0);
    setIsConfirmed(false);
    setError({});
    setResetKey((prev) => prev + 1);
  };

  const handleReset = () => {
    handleResetProperties();
  };

  const handleConfirmationClick = (value: boolean) => {
    if (value) {
      setIsConfirmed(true);
      setConsentMsg('I agree and bound to the terms and conditions');
    } else {
      setIsConfirmed(false);
      setConsentMsg('');
    }
  };

  const handlePreview = () => {
    setPreviewSurvey({
      ...(editSurvey as SurveyListItem),
    });
    setIsPreviewOpen(true);
  };

  const handleCreateOrUpdate = async () => {
    if (!surveyData.title?.trim()) {
      setError({ title: 'Please enter a survey title' });
      return;
    }

    if (!editSurvey && !odkFile && !surveyData.fileData) {
      setError({ file: 'Please upload a survey file' });
      return;
    }

    if (!isConfirmed) {
      setError({ terms: 'Please confirm the terms and conditions' });
      return;
    }

    setIsSubmitting(true);

    try {
      if (editSurvey) {
        let odkFileUrl = editSurvey.odkFileUrl;

        // Check if there's a new file (either odkFile or fileData)
        if (odkFile || surveyData.fileData) {
          setIsUploading(true);

          try {
            let fileBuffer: ArrayBuffer;
            let fileName: string;
            let mimeType: string;

            if (odkFile) {
              // Use the File object if available
              fileBuffer = await odkFile.arrayBuffer();
              fileName = `surveys/${Date.now()}_${odkFile.name}`;
              mimeType =
                odkFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            } else if (surveyData.fileData) {
              // Use the stored fileData
              fileBuffer = Uint8Array.from(atob(surveyData.fileData.content), (c) =>
                c.charCodeAt(0),
              ).buffer;
              fileName = `surveys/${Date.now()}_${surveyData.fileData.name}`;
              mimeType =
                surveyData.fileData.type ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            }

            const uploadedFile = await uploadBufferToS3({
              file: new File([fileBuffer!], fileName!, { type: mimeType! }),
              context: 'survey',
              contextId: surveyData.title,
            });
            odkFileUrl = uploadedFile.url;

            showToast.success('File uploaded successfully!');
          } catch (uploadError) {
            console.error('S3 upload error:', uploadError);
            showToast.error('Failed to upload file to S3. Please try again.');
            return;
          } finally {
            setIsUploading(false);
          }
        }

        const updateSurveyDto = {
          title: surveyData.title,
          consentMsg: consentMsg,
          odkFileUrl: odkFileUrl,
          status: surveyData.status,
        };

        const result = await dispatch(
          updateSurvey({
            id: String(editSurvey.id),
            data: updateSurveyDto,
          }),
        ).unwrap();

        setSurveyData((prev) => ({
          ...prev,
          id: result.data.id,
        }));

        setShowModal(true);
      } else {
        setIsUploading(true);

        let odkFileUrl = '';

        try {
          let fileBuffer: ArrayBuffer;
          let fileName: string;
          let mimeType: string;

          if (odkFile) {
            // Use the File object if available
            fileBuffer = await odkFile.arrayBuffer();
            fileName = `surveys/${Date.now()}_${sanitizeKey(odkFile.name)}`;
            mimeType =
              odkFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          } else if (surveyData.fileData) {
            // Use the stored fileData
            fileBuffer = Uint8Array.from(atob(surveyData.fileData.content), (c) =>
              c.charCodeAt(0),
            ).buffer;
            fileName = `surveys/${Date.now()}_${sanitizeKey(surveyData.fileData.name)}`;
            mimeType =
              surveyData.fileData.type ||
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          } else {
            throw new Error('No file available for upload');
          }

          const uploadedFile = await uploadBufferToS3({
            file: new File([fileBuffer], fileName, { type: mimeType }),
            context: 'survey',
            contextId: surveyData.title,
          });
          odkFileUrl = uploadedFile.url;

          showToast.success('File uploaded successfully! Creating survey...');
        } catch (uploadError) {
          console.error('S3 upload error:', uploadError);
          showToast.error('Failed to upload file to S3. Please try again.');
          return;
        } finally {
          setIsUploading(false);
        }

        const createSurveyDto = {
          title: surveyData.title,
          consentMsg: consentMsg,
          odkFileUrl: odkFileUrl,
        };
        const result = await dispatch(createSurvey(createSurveyDto)).unwrap();

        setSurveyData((prev) => ({
          ...prev,
          id: result.data.id,
        }));

        setShowModal(true);
      }
    } catch (error: any) {
      console.error('Error creating/updating survey:', error);
      showToast.error(error?.message || 'Failed to save survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageHeaderWrapper
      title={editSurvey ? 'Edit Survey' : 'Create New Survey'}
      description={editSurvey ? 'Update the survey details.' : 'Start off with a new Survey.'}
      onAdd={() => navigate(-1)}
      addLabel={editSurvey ? 'Return to Survey List' : 'Return to Survey List'}
      showExport={false}
    >
      {/* Stepper */}
      <Stepper
        className="gap-5"
        steps={[
          { title: 'Basic Information', subtitle: 'Enter basic survey details' },
          {
            title: editSurvey ? 'Review & Update' : 'Review & Create',
            subtitle: 'Final check & confirmation',
          },
        ]}
        activeStep={step}
      />

      {/* Form Card */}
      <div className="bg-white rounded-xl">
        <div className={step === 0 ? 'block' : 'hidden'}>
          <BasicInfo
            key={resetKey}
            survey={surveyData}
            onSurveyChange={setSurveyData}
            onFileData={setOdkFile}
            onDownloadFile={handleDownloadExistingFile}
            isDownloading={isDownloading}
            onPreview={handlePreview}
            error={error}
            setError={setError}
          />
        </div>
        <div className={step === 1 ? 'block' : 'hidden'}>
          <SurveyReviewAndCreate
            survey={surveyData}
            file={odkFile}
            isConfirmed={isConfirmed}
            setIsConfirmed={handleConfirmationClick}
            onDownloadFile={handleDownloadExistingFile}
            isDownloading={isDownloading}
          />
        </div>
        {error && (
          <div className="px-6 pb-4">
            <p className="text-red-500 text-sm font-medium">{error.terms}</p>
          </div>
        )}
        {/* Prev, Reset & Next */}
        <div className="p-6">
          <PrevNext
            hidePrevious={step === 0}
            prev={handleBackStep}
            next={handleNext}
            onReset={handleReset}
            create={step === 1}
            createLabel={editSurvey ? 'Update Survey →' : 'Create New Survey →'}
            onCreate={handleCreateOrUpdate}
            disableCreate={!isConfirmed}
            disableNext={isNextDisabled}
            disableReset={step === 1}
            isSubmitting={isSubmitting || isUploading}
          />
        </div>
      </div>

      {/* Survey Preview Modal */}

      {/* Success Modal */}
      <SurveySuccessModal
        isOpen={showModal}
        title={editSurvey ? 'Survey Updated Successfully!' : 'New Survey Created Successfully!'}
        dataFields={[
          { label: 'Survey ID', value: surveyData.id?.toString() || 'N/A' },
          { label: 'Survey Title', value: surveyData.title || 'N/A' },
        ]}
        actions={['File Uploaded', 'Database Updated', 'Survey Configured', 'Notifications Sent']}
        onClose={() => setShowModal(false)}
        onPrimaryAction={handleResetProperties}
        onSecondaryAction={() => {
          setShowModal(false);
          navigate('/dashboard/survey-management');
        }}
        primaryButtonText={editSurvey ? 'Edit Again' : 'Create Another Survey'}
        hidePrimaryButton={editSurvey !== undefined}
        secondaryButtonText="View Survey Lists"
      />

      <SurveyPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        getUpdatedPreviewSurvey={() => previewSurvey}
      />
    </PageHeaderWrapper>
  );
};

export default CreateSurvey;
